import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    TextInput,
    PhoneInput,
    Services,
    Modal,
    Text,
    BubbleSelectBlock,
    i18n,
    tokens,
    localizationMaps
} from '@simplybenefits/sb-ui-library';

import * as notifier from '../../../actions/notifier.js';
import { editAccount } from '../../../actions/admin.js';
import FormControl from "@mui/material/FormControl";

const statusMapString = {
    0: 'In Progress',
    1: 'Active',
    2: 'Disabled',
    9: 'Cancelled'
};

const statusMapInt = {
    "In Progress": 0,
    Active: 1,
    Disabled: 2,
    Cancelled: 9
};

class AccountSettings extends PureComponent {
    state = {
        addressLineTwo: '',
        addressLineOne: '',
        city: '',
        province: '',
        postalCode: '',
        country: 'Canada',
        companyName: '',
        contactEmail: '',
        phone: '',
        status: '',
        authorityName: '',
        authorityEmail: '',
        asoType: '',
        isAsoDental: '',
        isAsoHealth: '',
        enforceMFA: false
    };

    async componentDidMount() {
        const { account } = this.props;

        await this.setState({
            addressLineTwo: account.addressLineTwo || '',
            addressLineOne: account.addressLineOne || '',
            city: account.city || '',
            province: localizationMaps.fromEnglish("provinces", account.province) || '',
            postalCode: account.postalCode || '',

            companyName: account.company,
            contactEmail: account.contactEmail,
            phone: account.phone,
            status: statusMapString[account.status || 2],
            authorityName: account.authorityName,
            authorityEmail: account.authorityEmail,
            asoType: account.asoType,
            isAsoDental: account.isAsoDental,
            isAsoHealth: account.isAsoHealth,
            enforceMFA: account.enforceMFA || false
        });

        document.addEventListener("keydown", this._handleKeyPress, false);
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this._handleKeyPress, false);
    }

    _onBlur = async (type, event) => {        
        this.setState({
            [`${ type }Error`]: await Services.validateInput(type, this.state[type])
        });

        if (event) {
            this.setState({ [type]: event.target.value });
        }
    };

    _handleInputChange = (type, event, isValue = false) => {
        this.setState({
            [type]: isValue ? event : event.target.value
        });
    };

    _validateForm = async () => {
        const companyNameError = await Services.validateInput('companyName', this.state.companyName);
        const statusError = await Services.validateInput('status', this.state.status);
        const phoneError = await Services.validateInput('phone', this.state.phone);
        const contactEmailError = await Services.validateInput('contactEmail', this.state.contactEmail);
        const authorityNameError = await Services.validateInput('authorityName', this.state.authorityName);
        const authorityEmailError = await Services.validateInput('authorityEmail', this.state.authorityEmail);

        const addressLineOneError = await Services.validateInput('addressLineOne', this.state.addressLineOne);
        const cityError = await Services.validateInput('city', this.state.city);
        const provinceError = await Services.validateInput('province', this.state.province);
        const postalCodeError = await Services.validateInput('postalCode', this.state.postalCode);

        this.setState({
            companyNameError,
            phoneError,
            contactEmailError,
            authorityNameError,
            authorityEmailError,
            statusError,

            addressLineOneError,
            cityError,
            provinceError,
            postalCodeError
        });

        if (
            companyNameError ||
            phoneError ||
            contactEmailError ||
            authorityNameError ||
            authorityEmailError ||
            statusError ||

            addressLineOneError ||
            cityError ||
            provinceError ||
            postalCodeError
        ) {
            this.props.dispatch(notifier.displayError({ verbiage: i18n.t(tokens.labels.pleaseConfirmDetailsError) }));

            return false;
        }

        return true;
    };

    _saveAccount = async () => {
        const isFormValid = await this._validateForm();

        if (!isFormValid) {
            return false;
        }

        const {
            addressLineTwo,
            addressLineOne,
            city,
            province,
            postalCode,
            companyName,
            contactEmail,
            phone,
            status,
            authorityName,
            authorityEmail,
            enforceMFA
        } = this.state;

        const body = {
            company: companyName.trim(),
            addressLineOne: addressLineOne.trim(),
            addressLineTwo: addressLineTwo?.trim() || null,
            city: city.trim(),
            province: localizationMaps.toEnglish('provinces', province.trim()),
            postalCode,
            phone: phone.trim(),
            contactEmail: contactEmail.trim(),
            authorityName: authorityName.trim(),
            authorityEmail: authorityEmail.trim(),
            status: statusMapInt[status],
            enforceMFA: Boolean(enforceMFA)
        };

        const success = this.props.dispatch(editAccount(this.props.account.accountID, body));

        if (success) {
            this.props.hideModal();
            document.removeEventListener("keydown", this._handleKeyPress, false);
        }

        return true;
    };

    _handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            return this._saveAccount();
        }

        return false;
    };

    /**
     * 
     * @param {*} input 
     */
    _formatPostalCode = async (input) => {
        const formattedPostalCode = input
            .toUpperCase()
            .trim()
            .replace(/\s+/g, '') // Remove all spaces
            .replace(/([A-Z0-9]{3})([A-Z0-9]{3})/, '$1 $2'); // Add a space after the first 3 characters


        await this._handleInputChange('postalCode', formattedPostalCode, true);
        this._onBlur('postalCode');
    };

    render() {
        const { hideModal } = this.props;
        const {
            addressLineTwo,
            addressLineOne,
            city,
            province,
            postalCode
        } = this.state;
        
        const styles = {
            form: {
                display: 'flex',
                flexDirection: 'column'
            },
            row: {
                display: 'flex',
                flexDirection: 'row'
            },
            divider: {
                width: 40
            }
        };

        return (
            <Modal
                testid="account-settings-modal"
                open={ true }
                onClose={ hideModal }
                title={ i18n.t(tokens.labels.account.modifyDetails) }
                primaryButton={{
                    onClick: this._saveAccount
                }}
                loader={this.props.loader}
            >
                <TextInput
                    label={ i18n.t(tokens.labels.account.companyName) }
                    value={ this.state.companyName }
                    disabled
                    fullWidth
                    autofocus
                />

                <TextInput
                    label={ i18n.t(tokens.labels.addressLineOne) }
                    autocomplete="shipping street-address"
                    value={ addressLineOne }
                    onBlur={ () => this._onBlur('addressLineOne') }
                    onChange={ (value) => this._handleInputChange('addressLineOne', value) }
                    error={ Boolean(this.state.addressLineOneError) }
                    helperText={ this.state.addressLineOneError }
                    fullWidth
                />

                <div style={ styles.row }>
                    <TextInput
                        label={ i18n.t(tokens.labels.addressLineTwo) }
                        value={ addressLineTwo }
                        onBlur={ () => this._onBlur('addressLineTwo') }
                        onChange={ (value) => this._handleInputChange('addressLineTwo', value) }
                        fullWidth
                        helperText={i18n.t(tokens.labels.addressLineTwoExample)}
                    />

                    <div style={ styles.divider } />

                    <TextInput
                        label={ i18n.t(tokens.labels.account.cityTown) }
                        autocomplete="shipping locality"
                        value={ city }
                        onBlur={ () => this._onBlur('city') }
                        onChange={ (value) => this._handleInputChange('city', value) }
                        error={ Boolean(this.state.cityError) }
                        helperText={ this.state.cityError }
                        fullWidth
                    />
                </div>


                <div style={ styles.row }>
                    <TextInput
                        label={ i18n.t(tokens.labels.province) }
                        autocomplete="shipping region"
                        value={ province }
                        onBlur={() => this._onBlur('province')}
                        onChange={ (value) => this._handleInputChange('province', value) }
                        select
                        selectOptions={ Services.selectFieldOptions.provinceOptions() }
                        fullWidth
                    />

                    <div style={ styles.divider } />

                    <TextInput
                        label={ i18n.t(tokens.labels.postalCode) }
                        autocomplete="shipping postal-code"
                        value={ postalCode }
                        onBlur={(e) => this._formatPostalCode(e.target.value)}
                        onChange={(e) => {

                            // Allow typing only 6 alphanumeric characters and trim any excess
                            const postalCodeValue = e.target.value
                                .toUpperCase()
                                .slice(0, 7);
                            this._handleInputChange('postalCode', postalCodeValue, true);
                        }}
                        error={ Boolean(this.state.postalCodeError) }
                        helperText={ this.state.postalCodeError }
                        fullWidth
                    />
                </div>

                <PhoneInput
                    label={ i18n.t(tokens.labels.account.companyContactNumber) }
                    value={ this.state.phone }
                    onBlur={ () => this._onBlur('phone') }
                    onChange={ (value) => this._handleInputChange('phone', value) }
                    error={ Boolean(this.state.phoneError) }
                    helperText={ this.state.phoneError }
                    fullWidth
                />

                <TextInput
                    label={ i18n.t(tokens.labels.account.companyEmail) }
                    value={ this.state.contactEmail }
                    onBlur={ () => this._onBlur('contactEmail') }
                    onChange={ (value) => this._handleInputChange('contactEmail', value) }
                    error={ Boolean(this.state.contactEmailError) }
                    helperText={ this.state.contactEmailError }
                    fullWidth
                />

                <TextInput
                    label={ i18n.t(tokens.labels.account.signingAuthorityName) }
                    value={ this.state.authorityName }
                    onBlur={ () => this._onBlur('authorityName') }
                    onChange={ (value) => this._handleInputChange('authorityName', value) }
                    error={ Boolean(this.state.authorityNameError) }
                    helperText={ this.state.authorityNameError }
                    fullWidth
                />

                <TextInput
                    label={ i18n.t(tokens.labels.account.signingAuthorityEmail) }
                    value={ this.state.authorityEmail }
                    onBlur={ () => this._onBlur('authorityEmail') }
                    onChange={ (value) => this._handleInputChange('authorityEmail', value) }
                    error={ Boolean(this.state.authorityEmailError) }
                    helperText={ this.state.authorityEmailError }
                    fullWidth
                />

                <FormControl component="fieldset">
                    <Text
                        medium
                        light
                        margin="20px 0 0px 0"
                    >
                        { i18n.t(tokens.labels.account.enforceMFA) }
                    </Text>

                    <Text
                        tiny
                        light
                        margin="5px 0 5px 0"
                    >
                        { i18n.t(tokens.labels.account.enforceMFAMessage) }
                    </Text>

                    <BubbleSelectBlock
                        first={{
                            title: i18n.t(tokens.labels.yes),
                            selected: Boolean(this.state.enforceMFA) === true,
                            field: 'enforceMFAYes'
                        }}
                        second={{
                            title: i18n.t(tokens.labels.no),
                            selected: Boolean(this.state.enforceMFA) === false,
                            field: 'enforceMFANo'
                        }}
                        onBlur={(e) => this._onBlur('enforceMFA', e)}
                        handleChange={ async (field) => {
                            if (field === 'enforceMFAYes') {
                                await this._handleInputChange('enforceMFA', true, true);
                            } else {
                                await this._handleInputChange('enforceMFA', false, true);
                            }
                        }}
                    />
                </FormControl>

                {
                    (!this.props.account.isPrivateLabel && this.state.asoType !== null) &&
                    <>
                        <Text
                            medium
                            light
                            margin="10px 0 10px 0"
                        >
                            { i18n.t(tokens.labels.account.asoSettingsContact) }
                        </Text>

                        <FormControl component="fieldset">
                            <Text
                                small
                                bold
                                margin="10px 0 0 0"
                            >
                                { i18n.t(tokens.labels.account.asoType) }
                            </Text>

                            <BubbleSelectBlock
                                first={{
                                    title: i18n.t(tokens.labels.account.budgeted),
                                    selected: this.state.asoType === 'Budgeted',
                                    field: 'Budgeted'
                                }}
                                second={{
                                    title: i18n.t(tokens.labels.account.inArrears),
                                    selected: this.state.asoType === 'Arrears',
                                    field: 'Arrears'
                                }}
                                disabled={ true }
                            />
                        </FormControl>

                        <FormControl component="fieldset">
                            <Text
                                medium
                                bold
                                margin="10px 0 10px 0"
                            >
                                { i18n.t(tokens.labels.account.asoDental) }
                            </Text>

                            <BubbleSelectBlock
                                first={{
                                    title: i18n.t(tokens.buttons.yes),
                                    selected: Boolean(this.state.isAsoDental) === true,
                                    field: 'isAsoDentalYes'
                                }}
                                second={{
                                    title: i18n.t(tokens.buttons.no),
                                    selected: Boolean(this.state.isAsoDental) === false,
                                    field: 'isAsoDentalNo'
                                }}
                                disabled={ true }
                            />
                        </FormControl>

                        <FormControl component="fieldset">
                            <Text
                                medium
                                bold
                                margin="10px 0 10px 0"
                            >
                                { i18n.t(tokens.labels.account.asoHealth) }
                            </Text>

                            <BubbleSelectBlock
                                first={{
                                    title: i18n.t(tokens.buttons.yes),
                                    selected: Boolean(this.state.isAsoHealth) === true,
                                    field: 'isAsoHealthYes'
                                }}
                                second={{
                                    title: i18n.t(tokens.buttons.no),
                                    selected: Boolean(this.state.isAsoHealth) === false,
                                    field: 'isAsoHealthNo'
                                }}
                                disabled={ true }
                            />
                        </FormControl>
                    </>
                }
            </Modal>
        );
    }
}


AccountSettings.propTypes = {
    account: PropTypes.object,
    whitelabel: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    account: state.admin.account,
    whitelabel: state.whitelabel
});

export default (connect(mapStateToProps)(AccountSettings));